import type {Id, Store} from "tinybase/with-schemas";
import {ExternalStore} from "../utils/ExternalStore";

// Hydrated snapshots of a tinybase store for useSyncExternalStore. A snapshot keeps its identity
// until its data changes, so components only re-render for changes they read, and a row that
// didn't change stays the same object.
//
// Changes only mark the cache as stale. Subscribers hear about them once the whole transaction is
// done: tinybase calls a row listener per changed row, and reading the table after each one would
// rebuild it once per row.
//
// Only for hooks: inside a transaction the store is ahead of the cache until the transaction ends.

abstract class StoreSnapshots<T> extends ExternalStore<T> {
  private changed = false;
  constructor(store: Store<[any, any]>) {
    super();
    // runs after every other listener, so the cache is up to date when subscribers read it
    store.addDidFinishTransactionListener(() => {
      if (!this.changed) return;
      this.changed = false;
      this.notify();
    });
  }
  protected markChanged() {
    this.changed = true;
  }
}

export class TableSnapshots<TModel> extends StoreSnapshots<Record<Id, TModel>> {
  // null for ids without a row: a row listener also fires when the row gets added
  private rows = new Map<Id, TModel | null>();
  private all: Record<Id, TModel> | null = null;
  private ids: Id[] | null = null;

  constructor(
    private store: Store<[any, any]>,
    private tableId: string,
    private hydrate: (row: any) => TModel
  ) {
    super(store);
    store.addRowListener(tableId, null, (_store, _tableId, rowId) => {
      this.rows.delete(rowId);
      this.all = null;
      this.markChanged();
    });
    store.addRowIdsListener(tableId, () => {
      this.ids = null;
      this.markChanged();
    });
  }

  getRow = (id: Id): TModel | null => {
    let row = this.rows.get(id);
    if (row === undefined) {
      row = this.store.hasRow(this.tableId, id)
        ? this.hydrate(this.store.getRow(this.tableId, id))
        : null;
      this.rows.set(id, row);
    }
    return row;
  };

  getIds = (): Id[] => (this.ids ??= this.store.getRowIds(this.tableId));

  getSnapshot = (): Record<Id, TModel> =>
    (this.all ??= Object.fromEntries(
      this.store.getRowIds(this.tableId).map((id) => [id, this.getRow(id)!])
    ));
}

export class ValuesSnapshot<TModel> extends StoreSnapshots<TModel> {
  private snapshot: TModel | null = null;

  constructor(
    private store: Store<[any, any]>,
    private hydrate: (values: any) => TModel
  ) {
    super(store);
    store.addValuesListener(() => {
      this.snapshot = null;
      this.markChanged();
    });
  }

  getSnapshot = (): TModel => (this.snapshot ??= this.hydrate(this.store.getValues()));
}
